import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core'
import { FooterComponent } from '../../ui/footer/footer.component'
import { RouterOutlet } from '@angular/router'
import { BreadcrumbDatas, HeaderComponent } from '../../ui/header/header.component'
import { UserDomainService } from '../../../shared/domain/user.domain'
import { ParamDomainService } from '../../../shared/domain/param.domain'
import { Observable, of, switchMap } from 'rxjs'
import { AsyncPipe, DOCUMENT } from '@angular/common'
import { SelectOtherChantierModalComponent } from '../../ui/select-other-chantier-modal/select-other-chantier-modal.component'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { User } from '../../../shared/models/user'
import { ChantierDomainService } from '../../../shared/domain/chantier.domain'
import { EvenementDomainService } from '../../../shared/domain/evenement.domain'
import { ChantierDTO } from '../../../shared/data-access/http/dto/chantier-dto'
import { CoreDomainService } from '../../domain/core.domain'
import { LogoSetting } from '../../../shared/models/logo-setting'
import { GlobalDomainService } from '../../../shared/domain/global.domain'
import { DocumentationDomainService } from '../../../shared/domain/documentation.domain'
import { ContactCsm } from '../../../shared/models/contact-csm'

@Component({
    selector: 'app-app-layout',
    templateUrl: './app-layout.component.html',
    styleUrls: ['./app-layout.component.scss'],
    standalone: true,
    imports: [HeaderComponent, RouterOutlet, FooterComponent, AsyncPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppLayoutComponent implements OnInit {
    protected readonly selectedChantier$: Observable<ChantierDTO>
    protected readonly selectableChantiers$: Observable<ChantierDTO[]>
    protected readonly selectedChantierIsIris$: Observable<boolean>
    protected readonly backendVersion$: Observable<string>
    protected readonly supportEmail$: Observable<string>
    protected readonly chantierFromUrl$: Observable<ChantierDTO | null>
    protected readonly user: User
    protected shouldDisplayBreadcrumbInHeader$: Observable<boolean>
    protected breadcrumbDatas$: Observable<BreadcrumbDatas>
    protected shouldDisplayAccesChantierButton$: Observable<boolean>
    protected currentLogo$: Observable<LogoSetting>
    protected shouldDisplaySimplifiedInterface$: Observable<boolean>
    protected entiteCode$: Observable<string | null>
    protected shouldDisplayEntiteComponent$: Observable<boolean>
    protected readonly shouldDisplayChantierSelect$: Observable<boolean>
    protected readonly shouldDisplayContactCSM$: Observable<boolean>
    protected readonly isChantierSelectorDisabled$: Observable<boolean>
    protected readonly chantierCodeFromUrl$: Observable<string | null>
    protected readonly contactCSM$: Observable<ContactCsm>

    private readonly userDomainService: UserDomainService
    private readonly paramDomainService: ParamDomainService
    private readonly modalService: NgbModal
    private readonly chantierDomainService: ChantierDomainService
    private readonly evenementDomainService: EvenementDomainService
    private readonly coreDomainService: CoreDomainService
    private readonly globalDomainService: GlobalDomainService
    private readonly window: Window
    private documentationDomainService: DocumentationDomainService

    constructor() {
        this.userDomainService = inject(UserDomainService)
        this.paramDomainService = inject(ParamDomainService)
        this.modalService = inject(NgbModal)
        this.chantierDomainService = inject(ChantierDomainService)
        this.evenementDomainService = inject(EvenementDomainService)
        this.window = inject(DOCUMENT).defaultView
        this.coreDomainService = inject(CoreDomainService)
        this.globalDomainService = inject(GlobalDomainService)
        this.documentationDomainService = inject(DocumentationDomainService)

        this.backendVersion$ = this.coreDomainService.getVersionBackend()
        this.user = this.userDomainService.connectedUser()
        this.selectedChantier$ = this.chantierDomainService.selectedChantier$
        this.selectableChantiers$ = this.chantierDomainService.selectableChantiers$
        this.selectedChantierIsIris$ = this.chantierDomainService.selectedChantierIsIris$
        this.supportEmail$ = this.chantierDomainService.supportEmail$

        this.shouldDisplayBreadcrumbInHeader$ = this.coreDomainService.shouldDisplayBreadcrumbInHeader$
        this.breadcrumbDatas$ = this.coreDomainService.breadcrumbDatas$
        this.shouldDisplayAccesChantierButton$ = this.coreDomainService.shouldDisplayAccesChantierButton$
        this.currentLogo$ = this.globalDomainService.currentLogo$
        this.shouldDisplaySimplifiedInterface$ = this.coreDomainService.shouldDisplaySimplifiedInterface$
        this.shouldDisplayEntiteComponent$ = this.coreDomainService.shouldDisplayEntiteComponent$
        this.shouldDisplayChantierSelect$ = this.coreDomainService.shouldDisplayChantierSelect$
        this.shouldDisplayContactCSM$ = this.chantierDomainService.shouldDisplayContactCSM$
        this.isChantierSelectorDisabled$ = this.coreDomainService.isChantierSelectorDisabled$
        this.contactCSM$ = this.chantierDomainService.selectedChantierCSM$
        this.entiteCode$ = this.coreDomainService.entiteCode$
        this.chantierCodeFromUrl$ = this.coreDomainService.chantierCodeFromUrl$
        this.chantierFromUrl$ = this.coreDomainService.chantierCodeFromUrl$.pipe(
            switchMap((code) => {
                if (!code) return of(null)
                return this.chantierDomainService.getChantierByCode$(code)
            }),
        )
    }

    ngOnInit(): void {
        if (this.user) {
            this.paramDomainService.loadParams().subscribe()
        }
    }

    selectChantier(chantierCode: string): void {
        this.chantierDomainService.setSelectedChantier(chantierCode)
        this.documentationDomainService.loadBrouillonCountForSelectedChantier().subscribe()

        if (chantierCode) {
            this.userDomainService.setDernierChantierCode(chantierCode).subscribe()
            this.evenementDomainService.createSelectionChantierEvenement(chantierCode).subscribe()
        }
    }

    openSelectChantierModal(): void {
        this.modalService.open(SelectOtherChantierModalComponent)
    }

    logOut(): void {
        this.userDomainService.logOut()
    }

    onContactSupport(): void {
        const supportEmail = this.chantierDomainService.supportEmail()
        this.window.open(`mailto:${supportEmail}`)
    }

    onCopyChantierToClipboard(): void {
        this.chantierDomainService.copyChantierToClipboard()
    }
}
